import { useFileCreateUploadMutation } from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { errorMessages } from "@dashboard/intl";
import { useState } from "react";
import { useIntl } from "react-intl";

export async function uploadFileToStorage(signedUrl: string, file: File): Promise<boolean> {
  try {
    const response = await fetch(signedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
        "Content-Length": file.size,
      },
      body: file,
    });

    if (response.ok) {
      return true;
    } else {
      console.error("Upload failed with status: " + response.status);

      return false;
    }
  } catch (err) {
    console.error("Upload error:", err);

    return false;
  }
}

export const useStorageUpload = (
  onUploadComplete: (attributeId: string, fileUrl: string, contentType: string) => Promise<void>,
): [
  (attributeId: string, file: File) => Promise<void>,
  {
    loading: boolean;
  },
] => {
  const intl = useIntl();
  const notify = useNotifier();
  const [isFileUploading, setIsFileUploading] = useState(false);

  const onError = () => {
    notify({
      status: "error",
      title: intl.formatMessage(errorMessages.imgageUploadErrorTitle),
      text: intl.formatMessage(errorMessages.imageUploadErrorText),
    });
  };

  const [fileCreateUpload, fileCreateUploadOpts] = useFileCreateUploadMutation();

  const updateWithStorageUpload = async (attributeId: string, file: File) => {
    const contentType = file.type;
    const result = await fileCreateUpload({
      variables: {
        filename: file.name,
      },
    });

    if (result.errors?.length) {
      onError();

      return;
    }

    const uploadInfo = result.data?.fileCreateUpload;

    if (!uploadInfo) {
      onError();

      return;
    }

    const error = uploadInfo.errors?.[0];

    if (error) {
      onError();

      return;
    }

    try {
      setIsFileUploading(true);

      const fileUploaded = await uploadFileToStorage(uploadInfo.uploadUrl, file);

      if (!fileUploaded) {
        onError();

        return;
      }
    } catch (e) {
      onError();

      return;
    } finally {
      setIsFileUploading(false);
    }

    await onUploadComplete(attributeId, uploadInfo.uploadedFile.url, contentType);
  };

  const options = {
    loading: isFileUploading || fileCreateUploadOpts.loading,
  };

  return [updateWithStorageUpload, options];
};
